import { useCallback } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
  ColumnFiltersState,
  getFilteredRowModel,
  SortingState,
  getSortedRowModel,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Button } from "../ui/button";

import CustomPagination from "../../components/Table/pagination/Pagination";
import { useState } from "react";
import { Input } from "../ui/input";
import { CirclePlus, Loader } from "lucide-react";

import { useSearchParams } from "react-router-dom";
import { DatePickerWithRange } from "../DateRangePicker";
import { DateRange } from "react-day-picker";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  onClick?: () => void;
  btnTitle?: string;
  date?: DateRange | undefined;
  setDate?: React.Dispatch<React.SetStateAction<DateRange | undefined>>;
  exportExcel?: () => void;
  loading?: boolean;
  filters?: string;
  setFilters?: (value: string) => void;
  hasHeader?: boolean;
}

export default function DataTable<TData, TValue>({
  columns,
  data,
  onClick,
  btnTitle,
  date,
  setDate,
  exportExcel,
  loading,
  filters,
  setFilters,
  hasHeader,
}: DataTableProps<TData, TValue>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = searchParams.get("page") || "1";

  const initialPageIndex = parseInt(page, 10) - 1;

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const [rowSelection, setRowSelection] = useState({});

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setFilters,
    state: {
      sorting,
      columnFilters,
      rowSelection,
      globalFilter: filters,
    },
    initialState: {
      pagination: {
        pageIndex: initialPageIndex,
      },
    },
  });

  const goToPage = useCallback(
    (page: number) => {
      table.setPageIndex(page - 1);
      setSearchParams({ page: page.toString() });
      localStorage.setItem("currentPage", page.toString());
    },
    [setSearchParams, table]
  );

  return (
    <>
      {hasHeader && (
        <div className="flex flex-col gap-2 md:flex-row items-center pb-4 justify-between w-full">
          <Input
            placeholder="Filter name..."
            value={(columnFilters[0]?.value as string) || ""}
            onChange={(event) =>
              table.getColumn("name")?.setFilterValue(event.target.value)
            }
            className="max-w-sm"
          />
          <div className="flex flex-col md:flex-row items-center gap-3">
            {exportExcel && (
              <Button onClick={exportExcel} className="flex items-center gap-2">
                Export to Excel
                <PiMicrosoftExcelLogoFill />
              </Button>
            )}
            <DatePickerWithRange date={date} setDate={setDate} />
          </div>
          {onClick && (
            <Button
              onClick={onClick}
              variant="default"
              className="px-4 !rounded-lg app-btn !mt-0"
            >
              <CirclePlus className="mr-2 h-4 w-4" />
              {btnTitle}
            </Button>
          )}
        </div>
      )}
      <div className="rounded-md border">
        {" "}
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center mx-auto w-full"
                    >
                      <div className="h-24 flex items-center justify-center w-full">
                        <Loader /> <span className="ml-2">Loading...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </div>

      <CustomPagination
        totalPages={table.getPageCount()}
        currentPage={table.getState().pagination.pageIndex + 1}
        goToPage={goToPage}
      />
    </>
  );
}
